import { useState } from "react";
import DateSelector from "../../../components/shared/fields/DateSelector";
import GridCard from "../../../components/shared/gridCard/GridCard";
import ColdStorageKg from "../../../images/material_orange.svg";
import KgEntered from "../../../images/material_kg.svg";
import Datatable from "../../../components/shared/datatable/Datatable";
import Button from "../../../components/shared/buttons/button";
import { PlusSvg } from "../../../svgFiles/PlusSvg";
import PackingAndSortingModal from "./PackingAndSortingModal";
import OverViewModal from "../materialEntered/OverViewModal";
import useTranslator from "../../../hooks/useTranslator";
import {
  useGetBrokerageMaterialQuery,
  useGetBrokerageSortAndPackedSummaryQuery,
} from "../materialEntered/materialApi";
import { convertToUserTimezone, formatDateAndTime, getCurrentDate } from "../../../helpers/Utils";
import { PROJECT_DATE_FORMAT } from "../../../helpers/constants";

const PackingAndSorting = () => {
  const { _t } = useTranslator();  
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [overview, setOverView] = useState(false);

//  ############## API CALLS ############  

    const {
        data: brokerageMaterial,
        isLoading,
        isFetching,
        isError,
        error,
        refetch : refetchRecord 
    } = useGetBrokerageMaterialQuery({
        page,
        status: ["sorting", "counting","packaging","packed"],
        ...filter
    });

    const { data: summaryData } = useGetBrokerageSortAndPackedSummaryQuery({
      date,
    });


  const listItems = [
    {
      label: _t("total_enetered_kg", "Total Entered KG"),
      value: summaryData?.data?.total_entered_kg || 0,
      icon: KgEntered,
    },
    {
      label: _t("total_kg_sorted", "Total KG Sorted"),
      value: summaryData?.data?.total_kg_sorted || 0,
      icon: KgEntered,
    },
     {
      label: _t("total_kg_packed", "Total KG Packed"),
      value: summaryData?.data?.total_kg_packed || 0,
      icon: KgEntered,
    },
    {
      label: _t("packing_pending_kg", "Packing Pending (KG)"),
      value: summaryData?.data?.total_sorting_pending || 0,
      icon: ColdStorageKg,
      showDay: false,
    },
    {
      label: _t("total_prime_kg", "Total Prime KG"),
      value: summaryData?.data?.total_prime_kg || 0,
      icon: ColdStorageKg,
    },
    {
      label: _t("total_seconds_kg", "Total Seconds KG"),
      value: summaryData?.data?.total_seconds_kg || 0,
      icon: ColdStorageKg,
    },
    {
      label: _t("total_pieces_kg", "Total Pieces KG"),
      value: summaryData?.data?.total_pieces_kg || 0,
      icon: ColdStorageKg,
    },
    {
      label: _t("kg_rejected", "KG Rejected"),
      value: summaryData?.data?.total_rejected_kg || 0,
      icon: ColdStorageKg,
    },
    // {
    //   label: _t("sorting_pending_kg", "Sorting Pending (KG)"),
    //   value: summaryData?.data?.total_sorting_pending || 0,
    //   icon: ColdStorageKg,
    //   showDay: false,
    // },
   
  ];

  
  const handleEditCounting = (row) => {
    setShowModal(true);
    if(row.status == "packed"){
      setModalMode("view");
    }else{
      setModalMode("edit");
    }
    setSelectedRecord(row);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const columns = [
    {
      label: _t("date", "Date"),
      accessor: "entered_date_time",
      cell: ({ value }) => {
        return convertToUserTimezone(value);
      },
    },
    { label: _t("batch_code", "Batch Code"), accessor: "batch_code" },
    // { label: _t("supplier_name", "Supplier Name"), accessor: "supplier_name" },
    { label: _t("fruit_type", "Fruit Type"), accessor: "fruit_name" },
    { label: _t("fruit_type_variety", "Fruit Type Variety"), accessor: "fruit_type_variety" },
    { label: _t("kg_entered", "KG Entered"), accessor: "total_entered_kg" },
    { label: _t("prime_percentage", "Prime %"), accessor: "prime_percentage" },
    { 
      label: _t("packed_percentage", "Packed %"), 
      accessor: "packed_percentage",
      cell: ({ value, row }) => {
        const packedPercentage = parseFloat(value) || 0;
        const rejectedPercentage = parseFloat(row.rejected_percentage) || 0;
        const total = packedPercentage + rejectedPercentage;
        
        if (total > 98) {
          return (
            <span className="cmn_badge">
              {packedPercentage.toFixed(2)}%
            </span>
          );
        }
        
        return <span>{packedPercentage.toFixed(2)}%</span>;
      }
    },
    { 
      label: _t("rejected_percentage", "Rejected %"), 
      accessor: "rejected_percentage",
      cell: ({ value, row }) => {
        const packedPercentage = parseFloat(row.packed_percentage) || 0;
        const rejectedPercentage = parseFloat(value) || 0;
        const total = packedPercentage + rejectedPercentage;
        
        if (total > 98) {
          return (
            <span className="cmn_badge">
              {rejectedPercentage.toFixed(2)}%
            </span>
          );
        }
        
        return <span>{rejectedPercentage.toFixed(2)}%</span>;
      }
    },
    { 
      label: _t("lot_completion_percentage", "Lot Completion %"), 
      accessor: "lot_completion_prcnt",
      cell: ({ value, row }) => {
        const packedPercentage = parseFloat(row.packed_percentage) || 0;
        const rejectedPercentage = parseFloat(row.rejected_percentage) || 0;
        const total = packedPercentage + rejectedPercentage;
        
        if (total > 98) {
          return (
            <span className="cmn_badge">
              {value}%
            </span>
          );
        }
        
        return <span>{value}%</span>;
      }
    },
    {
      label: _t("status", "Status"),
      accessor: "status",
      cell: ({ value }) => {

        let displayText = value;

        if (value === "counting") {
          displayText = _t("unloading", "Unloading");
        } else if (value === "packaging") {
          displayText = _t("still_sorting_and_packing", "Still Sorting/Packing");
        } else if (value === "packed") {
          displayText = _t("packed", "Packed");
        }

        return (
          <span
            className={
              value === "packed"
                ? "cmn_badge"
                : "cmn_badge inactive"
            }
          >
            {displayText}
          </span>
        );
      },
    },
    {
      label: _t("overview", "Overview"),
      accessor: "status",
      cell: ({ row }) => (
        <>
          <Button
            size="small"
            label={
              row.status === "counting" || row.status === "packaging"
                ? _t("edit", "Edit")
                : _t("view", "View")
            }
            className={row.status === "packed" ? "borderRedBtn" : ""}
            onClick={
              row.status === "counting" || row.status === "packaging"
                ? () => handleEditCounting(row)
                : () => handleEditCounting(row)
            }
          />
        </>
      ),
    },
  ];

  return (
    <div>
      {showModal && (
        <PackingAndSortingModal
          show={showModal}
          setShow={setShowModal}
          mode={modalMode}
          record={selectedRecord}
        />
      )}
      {overview && (
        <OverViewModal
          show={overview}
          setShow={setOverView}
          mode={modalMode}
          record={selectedRecord}
        />
      )}
      <div className="overview cmn_card mb-3">
        <div className="overview_header d-flex gap-2 align-items-center">
          <h6 className="mb-0 flex-grow-1">
            {_t("packing_sorting_overview", "Packing & Sorting Overview")}
          </h6>
          <DateSelector setDate={setDate} />
        </div>
        <ul className="m-0 grid_card_wrapper">
          {listItems.map((list, index) => (
            <GridCard
              key={index}
              title={list.label}
              value={list.value}
              icon={list.icon}
              day={
                list.showDay !== false
                  ? currentDate === date
                    ? _t("today", "Today")
                    : formatDateAndTime(date ,PROJECT_DATE_FORMAT )
                  : ""
              }
            />
          ))}
        </ul>
      </div>

      <Datatable
        title={_t("packing_sorting_log", "Packing & Sorting Log")}
        columns={columns}
        tableData={brokerageMaterial?.data}
        pagination
        page={page}
        onPageChange={handlePageChange}
        showFilter={setFilter}
        showFilterButton={false}
        filterModule="packing-sorting-filter"
      />
    </div>
  );
};

export default PackingAndSorting;
